import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCurrency, currencyData } from "../context/CurrencyContext";
import { FiGlobe, FiDollarSign, FiFacebook, FiInstagram } from "react-icons/fi";
import { SiTiktok } from "react-icons/si";
import api from "../services/api";
import visa from "../assets/visa.png";
import mastercard from "../assets/mastercard.png";

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { currency } = useCurrency();

  const socialLinks = {
    tiktok: "https://www.tiktok.com/@chafadi2?_r=1&_t=ZS-95drU5V5WMC",
    facebook: "https://www.facebook.com/share/1AVc5SWEcG/?mibextid=wwXIfr",
    instagram: "https://www.instagram.com/chafadicom?igsh=NnBrMTFna2l4a2th&utm_source=qr",
  };

  const socialIcons = {
    tiktok: <SiTiktok size={14} />,
    facebook: <FiFacebook size={14} />,
    instagram: <FiInstagram size={14} />,
  };

  return (
    <footer className="bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] py-14 border-t border-[var(--color-border)]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-10 lg:gap-8 pb-12">
          {/* Brand Section */}
          <div className="lg:col-span-4 space-y-4">
            <img src="/logo.png" alt="Chafadi" className="h-7 w-auto opacity-90" />
            <p className="text-[13px] text-[var(--color-text-secondary)] leading-relaxed max-w-sm">
              {t(
                "footer.brandText",
                "Connecting trusted suppliers across Niger. Trade smarter, trade better."
              )}
            </p>
            <div className="flex items-center gap-2.5 pt-1">
              {["tiktok", "facebook", "instagram"].map((social) => (
                <a
                  key={social}
                  href={socialLinks[social]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] hover:border-[var(--color-accent)] transition-all"
                >
                  {socialIcons[social]}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-primary)]">
              {t("footer.linksTitle", "Company")}
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: t("footer.aboutUs", "About"), path: "/about" },
                { label: t("footer.terms", "Terms"), path: "/terms-of-service" },
                { label: t("footer.privacy", "Privacy"), path: "/privacy-policy" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[12px] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Marketplace */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-primary)]">
              {t("footer.marketplaceTitle", "Marketplace")}
            </h3>
            <ul className="space-y-2.5">
              {[
                { label: t("footer.shop", "Browse Store"), path: "/search/" },
                { label: t("footer.newArrivals", "New Arrivals"), path: "/new-arrivals" },
                { label: t("footer.bestSellers", "Featured Deals"), path: "/flash-deals" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[12px] text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors no-underline"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Preferences */}
          <div className="lg:col-span-4 space-y-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-primary)]">
              {t("footer.preferencesTitle", "Preferences")}
            </h3>
            <div className="flex flex-wrap items-center gap-3">
              <div className="bg-[var(--color-bg)] px-3 py-1.5 rounded-full flex items-center gap-1.5 border border-[var(--color-border)]">
                <FiDollarSign size={13} className="text-[var(--color-accent)]" />
                <span className="text-[11px] font-semibold text-[var(--color-text-primary)]">
                  {currencyData[currency]?.symbol || "CFA"}
                </span>
              </div>

              <div className="relative group">
                <FiGlobe
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-accent)] z-10"
                  size={13}
                />
                <select
                  className="h-8 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-full pl-8 pr-6 text-[11px] font-medium text-[var(--color-text-primary)] outline-none appearance-none cursor-pointer hover:border-[var(--color-accent)] transition-all"
                  value={i18n.language === "fr" ? "fr" : "en"}
                  onChange={async (e) => {
                    const normalized = e.target.value === "fr" ? "fr" : "en";
                    i18n.changeLanguage(normalized);
                    localStorage.setItem("chafadi-lang", normalized);
                    if (localStorage.getItem("chafadi-token")) {
                      try {
                        await api.patch("/me/language/", { language: normalized });
                      } catch (_) {}
                    }
                  }}
                >
                  <option value="en">English (EN)</option>
                  <option value="fr">Français (FR)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 border-t border-[var(--color-border)] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[11px] text-[var(--color-text-secondary)]">
            © {new Date().getFullYear()} Chafadi. {t("footer.copyright", "All rights reserved.")}
          </p>

          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3 opacity-60">
              <img src={visa} alt="Visa" className="h-3.5 w-auto" />
              <img src={mastercard} alt="Mastercard" className="h-4 w-auto" />
            </div>
            <div className="h-3 w-px bg-[var(--color-border)] hidden md:block" />
            <div className="flex items-center gap-2 text-[10px] text-[var(--color-text-secondary)]">
              <span>Secure Checkout</span>
              <span>•</span>
              <span className="font-semibold text-[var(--color-accent)]">Niamey, Niger</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
